import React, { useEffect, useState } from "react";
import { MoreVertical } from "lucide-react";
import { useAddress } from "../../context/AddressContext";

function MyAddress() {
  const { addresses, fetchAddresses, addAddress, updateAddress, deleteAddress } =
    useAddress();

  const [openMenu, setOpenMenu] = useState(null);
  const [view, setView] = useState("list");
  const [editAddress, setEditAddress] = useState(null);
  const [deleteId, setDeleteId] = useState(null);


  useEffect(() => {
    fetchAddresses();
  }, []);

  const handleSave = (newAddr) => {
    addAddress(newAddr);
    setView("list");
  };

  const handleUpdate = (id, updatedAddr) => {
    updateAddress(id, updatedAddr);
    setView("list");
    setEditAddress(null);
  };

  const handleEdit = (addr) => {
    setEditAddress(addr);
    setView("edit");
    setOpenMenu(null);
  };

  const confirmDelete = () => {
    deleteAddress(deleteId);
    setDeleteId(null);
    setOpenMenu(null);
  };

  // ⚡ Set as primary
  const handleSetPrimary = (addr) => {
    updateAddress(addr.id, { ...addr, isPrimary: true });
  };

  if (view === "add" || view === "edit") {
    return (
      <AddEditAddress
        onSave={
          view === "edit"
            ? (addr) => handleUpdate(editAddress.id, addr)
            : handleSave
        }
        onCancel={() => setView("list")}
        title={view === "edit" ? "Edit Address" : "Add Address"}
        existing={view === "edit" ? editAddress : null}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 mx-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-blue-900">My Address</h1>
        <button
          onClick={() => setView("add")}
          className="flex items-center gap-2 text-blue-900 font-semibold border border-blue-500 px-3 py-1 rounded-lg hover:bg-blue-100 transition ml-20"
        >
          <span className="text-xl">+</span> Add Address
        </button>
      </div>

      {/* Address List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        {addresses.map((addr) => (
          <div
            key={addr.id}
            className="bg-white p-4 rounded-xl shadow-sm border flex justify-between items-start relative w-full min-h-[140px] transition-all"
          >
            <div className="pr-8">
              <h2 className="font-bold text-blue-900">{addr.name}</h2>
              <p className="text-gray-600 text-sm mt-1">
                {addr.address}, {addr.city}, {addr.country}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {addr.countryCode} {addr.phone}
              </p>
            </div>

            <div className="flex flex-col items-end">
              {/* Primary Selector */}
              <button
                onClick={() => handleSetPrimary(addr)}
                className="mb-2"
                title="Set as primary"
              >
                <span
                  className={`w-3 h-3 rounded-full block ${
                    addr.isPrimary ? "bg-green-500" : "bg-gray-300"
                  }`}
                />
              </button>

              {/* More Menu */}
              <div className="relative">
                <button
                  onClick={() =>
                    setOpenMenu(openMenu === addr.id ? null : addr.id)
                  }
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  <MoreVertical size={18} className="text-gray-600" />
                </button>

                {openMenu === addr.id && (
                  <div className="absolute top-8 right-0 bg-white border shadow-md rounded-lg py-2 w-32 text-sm z-50">
                    <button
                      onClick={() => handleEdit(addr)}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteId(addr.id)}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-lg z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Are you sure you want to delete this address?
            </h2>
            <div className="flex gap-4">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg font-semibold"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



function AddEditAddress({ onSave, onCancel, title, existing }) {
  const [formData, setFormData] = useState(
    existing || {
      name: "",
      phone: "",
      countryCode: "+91",
      address: "",
      pin: "",
      city: "",
    }
  );
  const [isPrimary, setIsPrimary] = useState(existing?.isPrimary || false);
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
  switch (name) {
    case "name":
      if (!value.trim()) return "Full Name is required";
      if (!/^[A-Za-z\s]+$/.test(value)) return "Name can only contain letters and spaces";
      if (value.trim().length < 3) return "Name must be at least 3 characters";
      return "";
    case "phone":
      if (!value.trim()) return "Phone number is required";
      if (!/^\d{10}$/.test(value)) return "Phone number must be 10 digits";
      return "";
    case "address":
      if (!value.trim()) return "Street Address is required";
      return "";
    case "pin":
      if (value && !/^\d{6}$/.test(value)) return "Postal code must be 6 digits";
      return "";
    case "city":
      if (!value.trim()) return "City is required";
      return "";
    default:
      return "";
  }
};



  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: validateField(name, value) });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return; // stop submit if errors exist

    const payload = { ...formData, isPrimary };
    onSave(payload);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex flex-col items-center">
      <div className="bg-white shadow-md rounded-xl p-6 w-full max-w-2xl">
        <h1 className="text-xl font-bold text-blue-900 mb-4">{title}</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name + Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full Name"
                className={`border-b p-2 outline-none ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                autoFocus
              />
              {errors.name && <span className="text-red-500 text-sm">{errors.name}</span>}
            </div>

            <div className="flex flex-col">
              <div className="flex items-center gap-2 border-b p-2">
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className="outline-none bg-transparent text-gray-700"
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+61">+61</option>
                  <option value="+81">+81</option>
                </select>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                   onChange={(e) => {
                      const onlyNums = e.target.value.replace(/\D/g, ""); 
                      handleChange({ target: { name: "phone", value: onlyNums } });
                    }}
                  placeholder="Contact Number"
                  className={`flex-1 outline-none ${
                    errors.phone ? "border-b border-red-500" : ""
                  }`}
                />
              </div>
              {errors.phone && <span className="text-red-500 text-sm">{errors.phone}</span>}
            </div>
          </div>

          {/* Address + Pin */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street Address"
                className={`border-b p-2 outline-none ${
                  errors.address ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.address && <span className="text-red-500 text-sm">{errors.address}</span>}
            </div>

            <div className="flex flex-col">
              <input
                type="text"
                name="pin"
                value={formData.pin}
                 onChange={(e) => {
                  const onlyNums = e.target.value.replace(/\D/g, "");
                  handleChange({ target: { name: "pin", value: onlyNums } });
                }}
                placeholder="Postal Code"
                className={`border-b p-2 outline-none ${
                  errors.pin ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.pin && <span className="text-red-500 text-sm">{errors.pin}</span>}
            </div>
          </div>

          {/* City */}
          <div className="flex flex-col">
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
              className={`border-b p-2 outline-none ${
                errors.city ? "border-red-500" : "border-gray-300"
              } w-full`}
            />
            {errors.city && <span className="text-red-500 text-sm">{errors.city}</span>}
          </div>

          {/* Primary Switch */}
          <div className="flex items-center justify-between">
            <span className="text-blue-900 font-semibold">Save as primary address</span>
            <button
              type="button"
              onClick={() => setIsPrimary(!isPrimary)}
              className={`w-10 h-5 flex items-center rounded-full p-1 transition ${
                isPrimary ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`w-4 h-4 bg-white rounded-full shadow-md transform transition ${
                  isPrimary ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 mt-4">
            <button
              type="submit"
              className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-semibold shadow-md"
            >
              Save Address
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-lg font-semibold shadow-md"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}




export default MyAddress;
